import {
  compact,
  find,
  flatMap,
  fromPairs,
  isObject,
  isPlainObject,
  map,
  mapValues,
  pickBy,
  reject,
  some,
  take,
  trimStart,
  truncate,
} from "lodash";
import { AiccPromptVariable } from "types/model/aiccPrompt";
import { AiccSandboxInputs } from "types/model/aiccPromptSandbox";

// 🔴 Sent as `nickname` for the backend's `profile_slot`; the raw `preferred_name` once read to the model as its own name.
const VARIABLE_RENAME: Record<string, string> = {
  user_preferred_name: "nickname",
};

export const FLUENCY_VARIABLE = "fluency_level";

// 🔴 The backend composes these itself (`BACKEND_COMPOSED_SLOTS`) and ignores a value sent for them.
const COMPOSED_VARIABLES = ["beginner_rules"];

export const LANGUAGE_VARIABLE = "language";

const DATE_VARIABLE = "current_date";

const FLATTENED_VARIABLE = "user_profile";

const FLATTENED_PREFIX = "Profile: ";

// 🔴 An antd Input in the 336px column of the 720px drawer shows about 44 characters.
const LONG_EXAMPLE = 40;

export interface SandboxVariableField {
  name: string;
  description?: string;
  exampleValue?: string;
  isRequired: boolean;
  isLong: boolean;
  isFluency: boolean;
}

const profileKeys = (exampleValue?: string): Record<string, string> | undefined => {
  const body = trimStart(exampleValue ?? "").replace(FLATTENED_PREFIX, "");

  if (!body.trim().startsWith("{")) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(body);

    return isPlainObject(parsed)
      ? mapValues(parsed as Record<string, unknown>, (value) =>
          isObject(value) ? JSON.stringify(value) : String(value ?? "")
        )
      : undefined;
  } catch {
    return undefined;
  }
};

const toField = (variable: AiccPromptVariable): SandboxVariableField => ({
  name: variable.name,
  description: variable.description || undefined,
  exampleValue: variable.exampleValue,
  isRequired: variable.isRequired,
  isLong: (variable.exampleValue?.length ?? 0) > LONG_EXAMPLE,
  isFluency: variable.name === FLUENCY_VARIABLE,
});

export const sandboxVariableFields = (variables: AiccPromptVariable[]): SandboxVariableField[] =>
  flatMap(
    reject(variables, ({ name }) => COMPOSED_VARIABLES.includes(name)),
    (variable) => {
      const keys = variable.name === FLATTENED_VARIABLE ? profileKeys(variable.exampleValue) : undefined;

      return keys
        ? map(keys, (exampleValue, name) => toField({ name, description: "", exampleValue, isRequired: false }))
        : [toField(variable)];
    }
  );

const SUMMARY_VALUES = 2;

export const defaultSandboxInputs = (variables: AiccPromptVariable[]): AiccSandboxInputs =>
  fromPairs(
    compact(map(sandboxVariableFields(variables), ({ name, exampleValue }) => exampleValue && [name, exampleValue]))
  );

export const fixedVariableValues = (variables: AiccPromptVariable[], languageName?: string): AiccSandboxInputs => {
  const date = find(variables, { name: DATE_VARIABLE })?.exampleValue;

  return {
    ...(languageName && some(variables, { name: LANGUAGE_VARIABLE }) ? { [LANGUAGE_VARIABLE]: languageName } : {}),
    ...(date ? { [DATE_VARIABLE]: date } : {}),
  };
};

export const inputsSummary = (inputs: AiccSandboxInputs): string => {
  const values = compact(map(inputs, (value) => value?.trim()));
  const shown = map(take(values, SUMMARY_VALUES), (value) => truncate(value, { length: 24, omission: "…" }));
  const rest = values.length - shown.length;

  return compact([...shown, rest > 0 ? `+${rest} more` : ""]).join(" · ");
};

// 🔴 Empty boxes are omitted, since the backend treats absent as None. An unparsable `fluency_level` silently falls back to 1.
export function sandboxInputsRequest(inputs: AiccSandboxInputs) {
  const filled = pickBy(inputs, (value) => !!value?.trim());
  const variables = fromPairs(map(filled, (value, key) => [VARIABLE_RENAME[key] ?? key, value.trim()]));

  return { variables: Object.keys(variables).length ? variables : undefined };
}
